import { SceneService } from '@src/services/SceneService';
import { LogManager } from '@src/utility/Logger';

import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { AnchorResolver } from '../models/layout/AnchorResolver';
import { EditorChangeType } from '../types';
import { AbstractElementCommand } from './AbstractCommand';
import { SplitSceneCommand } from './SplitSceneCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { SplitInfo } from '@src/services/SceneService';

export class SplitElementCommand extends AbstractElementCommand {
  private splitTime: number;

  // scene-related
  private sceneService = new SceneService();
  private anchorResolver = new AnchorResolver();
  private splitInfoPartial: Omit<
    SplitInfo,
    'leftElementId' | 'rightElementId'
  > | null = null;
  private splitSceneSubCommand: SplitSceneCommand | null = null;

  private readonly _logger = LogManager.getLogger(
    'command:SplitElementCommand',
  );

  getName(): string {
    return 'SplitElementCommand';
  }
  private firstResultElement: Element | null = null;
  private secondResultElement: Element | null = null;
  private trackId: string | null | undefined = null;

  // extraElementData undo tracking
  private copiedElementIds: string[] = [];
  private copiedLogoKeys: string[] = [];

  constructor(
    editor: VideoEditor,
    element: Element,
    splitTime: number,
    snapToFrame: boolean = true,
  ) {
    super(editor, element);
    this.splitTime = snapToFrame ? this.snapTimeToFrame(splitTime) : splitTime;
  }

  execute(): boolean {
    // Store the track for undo
    this.trackId = this.editor.timeline.getTrackIdForElement(
      this.sourceElement.id,
    );
    if (!this.trackId) return false;

    // Capture split info BEFORE splitting (only for VideoTranscriptElement)
    if (this.isTranscriptElement()) {
      this.captureSplitInfo();
    }

    const result = this.performSplitOperation();
    if (!result) return false;

    // Store the resulting elements for undo and notification
    [this.firstResultElement, this.secondResultElement] = result;

    // Copy extraElementData from original to both split elements
    this.copyExtraElementDataToSplitElements();

    // Adjust scenes AFTER splitting
    if (this.isTranscriptElement() && this.splitInfoPartial) {
      this.adjustScenesAfterSplit();
    }

    // Split scene at the split time
    if (this.isTranscriptElement()) {
      this.splitSceneAtSplitTime();
    }

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the split
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime,
    });

    return true;
  }

  undo(): boolean {
    if (
      !this.firstResultElement ||
      !this.secondResultElement ||
      !this.trackId
    ) {
      return false;
    }

    // Clean up copied extraElementData entries
    for (const elementId of this.copiedElementIds) {
      this.extraElementData.removeElementData(elementId);
    }
    for (const logoKey of this.copiedLogoKeys) {
      this.extraElementData.removeElementData(logoKey);
    }

    // Remove both split elements
    this.editor.timeline.removeElement(this.firstResultElement.id);
    this.editor.timeline.removeElement(this.secondResultElement.id);

    // Restore the original element
    this.editor.timeline.addElementToTrack(this.trackId, this.sourceElement);

    // Note: Scene restoration for undo is not implemented yet

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the undo
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT_UNDONE, {
      originalElement: this.sourceElement,
      firstElement: this.firstResultElement,
      secondElement: this.secondResultElement,
      splitTime: this.splitTime,
    });

    return true;
  }

  /**
   * Performs the split operation using the appropriate track manager
   * @returns Array with split result elements, or null if failed
   */
  private performSplitOperation(): [Element, Element] | null {
    if (this.isTranscriptElement()) {
      return this.splitTranscriptElement();
    } else {
      return this.splitRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Splits a VideoTranscriptElement using TranscriptTrackManager
   */
  private splitTranscriptElement(): [Element, Element] | null {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    return transcriptTrackManager.splitElement(
      this.sourceElement as VideoTranscriptElement,
      this.splitTime,
    );
  }

  /**
   * Splits a regular element using TrackManager
   */
  private splitRegularElement(): [Element, Element] | null {
    const trackManager = this.editor.getTrackManager();
    return trackManager.splitElement(this.sourceElement, this.splitTime);
  }

  /**
   * Captures split info BEFORE element is split (for VideoTranscriptElement only)
   */
  private captureSplitInfo(): void {
    const element = this.sourceElement as VideoTranscriptElement;

    // Calculate split media time relative to element's media
    const splitMediaTime =
      element.getMediaStartTime() + (this.splitTime - element.time);

    this.splitInfoPartial = {
      originalElementId: element.id,
      originalMediaStart: element.getMediaStartTime(),
      originalMediaEnd: element.getMediaEndTime(),
      splitMediaTime,
    };
  }

  /**
   * Adjusts scenes AFTER element is split using captured info
   */
  private adjustScenesAfterSplit(): void {
    if (
      !this.splitInfoPartial ||
      !this.firstResultElement ||
      !this.secondResultElement
    ) {
      return;
    }

    const leftElement = this.firstResultElement as VideoTranscriptElement;
    const rightElement = this.secondResultElement as VideoTranscriptElement;

    const splitInfo: SplitInfo = {
      ...this.splitInfoPartial,
      leftElementId: leftElement.id,
      rightElementId: rightElement.id,
    };

    this.sceneService.adjustScenesAfterSplit(this.editor.timeline, splitInfo);
  }

  getDescription(): string {
    return `Split element ${this.sourceElement.id} at ${this.splitTime}s`;
  }

  // Expose the result elements for commandSplitElement to return
  getResultElements(): [Element, Element] | null {
    if (!this.firstResultElement || !this.secondResultElement) {
      return null;
    }
    return [this.firstResultElement, this.secondResultElement];
  }

  /**
   * Copies extraElementData from the original element to both split elements.
   * Delegates to ExtraElementData.propagateOnSplit() for the actual copying,
   * and tracks created keys for undo cleanup.
   */
  private copyExtraElementDataToSplitElements(): void {
    if (!this.firstResultElement || !this.secondResultElement) return;

    const { copiedElementIds, copiedLogoKeys } =
      this.extraElementData.propagateOnSplit(
        this.sourceElement,
        this.firstResultElement,
        this.secondResultElement,
      );

    this.copiedElementIds = copiedElementIds;
    this.copiedLogoKeys = copiedLogoKeys;
  }

  /**
   * Splits the scene at the split time if one exists.
   * Called AFTER adjustScenesAfterSplit has updated scene anchors.
   * Uses SplitSceneCommand as a sub-command to avoid duplicating logic.
   */
  private splitSceneAtSplitTime(): void {
    if (!this.trackId) {
      return;
    }

    // Find scene at split time on the transcript track
    const sceneAtSplitTime = this.sceneService.findSceneAtTimelineTime(
      this.editor.timeline,
      this.trackId,
      this.splitTime,
    );

    if (!sceneAtSplitTime) {
      this._logger.debug(
        `No scene found at split time ${this.splitTime}s on track ${this.trackId}`,
      );
      return;
    }

    try {
      // Calculate scene start time to compute relative time for SplitSceneCommand
      const sceneStartTime = this.anchorResolver.resolve(
        sceneAtSplitTime.anchorStart,
        this.editor.timeline,
      );
      const sceneEndTime = this.anchorResolver.resolve(
        sceneAtSplitTime.anchorEnd,
        this.editor.timeline,
      );

      // Validate split time is within scene bounds
      if (this.splitTime <= sceneStartTime || this.splitTime >= sceneEndTime) {
        this._logger.debug(
          `Split time ${this.splitTime}s is at scene boundary, not splitting scene`,
        );
        return;
      }

      // Calculate relative time within scene for SplitSceneCommand
      const relativeTime = this.splitTime - sceneStartTime;

      // Create and execute SplitSceneCommand as sub-command
      // Execute directly without going through executeCommand (parent handles undo)
      this.splitSceneSubCommand = new SplitSceneCommand(
        this.editor,
        sceneAtSplitTime.id,
        relativeTime,
      );

      const success = this.splitSceneSubCommand.execute();
      if (success) {
        this._logger.debug(
          `Split scene ${sceneAtSplitTime.id} at relative time ${relativeTime}s`,
        );
      } else {
        this._logger.warn(
          `Failed to split scene ${sceneAtSplitTime.id} at relative time ${relativeTime}s`,
        );
        this.splitSceneSubCommand = null;
      }
    } catch (error) {
      this._logger.error('Failed to split scene', { error });
    }
  }
}
